import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { apiUrl } from '../utils/apiBase';

const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  const activateSubscription = useCallback(async (subscriptionId) => {
    try {
      const response = await fetch(apiUrl(`/subscriptions/success?subscription_id=${subscriptionId}`), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      const data = await response.json();
      
      if (data.success) {
        setSubscription(data.data.subscription);
        toast.success(t('subscriptionSuccessPage.toastActivated'));
        
        // Redirect back to subscription management after a short delay
        setTimeout(() => {
          navigate('/subscription');
        }, 1500);
      } else {
        toast.error(data.message || t('subscriptionSuccessPage.toastFailed'));
        navigate('/subscription');
      }
    } catch (error) {
      console.error('Error activating subscription:', error);
      toast.error(t('subscriptionSuccessPage.toastError'));
      navigate('/subscription');
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    const subscriptionId = searchParams.get('subscription_id');
    if (!subscriptionId) {
      toast.error(t('subscriptionSuccessPage.toastInvalidId'));
      navigate('/subscription');
      return;
    }

    activateSubscription(subscriptionId);
  }, [navigate, searchParams, activateSubscription, t]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-700">{t('subscriptionSuccessPage.activating')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-md p-8 w-full max-w-md text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('subscriptionSuccessPage.title')}</h1>
          <p className="text-gray-600">{t('subscriptionSuccessPage.subtitle')}</p>
        </div>

        {subscription && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-2">{t('subscriptionSuccessPage.detailsTitle')}</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p><strong>{t('subscriptionSuccessPage.plan')}:</strong> {subscription.subscription_plan?.name}</p>
              <p><strong>{t('subscriptionSuccessPage.amount')}:</strong> ${subscription.amount} {subscription.currency}</p>
              <p><strong>{t('subscriptionSuccessPage.billing')}:</strong> {subscription.type}</p>
              <p><strong>{t('subscriptionSuccessPage.maxProperties')}:</strong> {subscription.max_properties}</p>
              <p><strong>{t('subscriptionSuccessPage.status')}:</strong> <span className="text-green-600 font-semibold">{subscription.status}</span></p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={() => navigate('/subscription')}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            {t('subscriptionSuccessPage.goToSubscription')}
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            {t('subscriptionSuccessPage.goToHome')}
          </button>
        </div>

        <p className="text-sm text-gray-500 mt-4">
          {t('subscriptionSuccessPage.autoRedirect')}
        </p>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;

